import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import { ButtonComponent, ButtonVariant, CheckboxComponent } from "@sxm-content-creators/tools-ds";
import { SnackbarService } from "../../../../components/snackbar/snackbar.service";
import { SubmissionFreeformApiService } from "../../services/submission-freeform.api.service";
import { SubmissionFreeformResponse } from "../../models/submission-freeform.model";

export interface DisplaySettingsModalData {
    submission: SubmissionFreeformResponse;
}

@Component({
    selector: "app-display-settings-modal",
    templateUrl: "./display-settings-modal.component.html",
    styleUrl: "./display-settings-modal.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ButtonComponent, CheckboxComponent]
})
export class DisplaySettingsModalComponent {
    readonly data = inject<DisplaySettingsModalData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef<DisplaySettingsModalComponent, boolean>);
    private readonly submissionFreeformApi = inject(SubmissionFreeformApiService);
    private readonly snackbar = inject(SnackbarService);

    private readonly initialIsPublic = this.data.submission.isPublic;
    private readonly initialIsFeatured = this.data.submission.isFeatured;

    readonly isPublic = signal(this.initialIsPublic);
    readonly isFeatured = signal(this.initialIsFeatured);
    readonly isSubmitting = signal(false);

    readonly hasChanges = computed(
        () => this.isPublic() !== this.initialIsPublic || this.isFeatured() !== this.initialIsFeatured
    );

    togglePublic(): void {
        const next = !this.isPublic();
        this.isPublic.set(next);
        if (!next) this.isFeatured.set(false);
    }

    toggleFeatured(): void {
        this.isFeatured.update(featured => !featured);
    }

    update(): void {
        if (!this.hasChanges() || this.isSubmitting()) return;

        this.isSubmitting.set(true);
        this.dialogRef.disableClose = true;

        this.submissionFreeformApi
            .updateDisplaySettings(this.data.submission.id, this.isPublic(), this.isFeatured())
            .subscribe({
                next: () => {
                    this.snackbar.show("Display Settings Updated.", "success");
                    this.dialogRef.close(true);
                },
                error: (error: HttpErrorResponse) => {
                    this.isSubmitting.set(false);
                    this.dialogRef.disableClose = false;
                    this.snackbar.show(error.error?.detail ?? "We couldn't update the display settings.", "error");
                }
            });
    }

    close(): void {
        if (this.isSubmitting()) return;
        this.dialogRef.close();
    }

    protected readonly ButtonVariant = ButtonVariant;
}
